import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { CalendarPlus, Check, ChevronLeft, ExternalLink, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import {
  asDay,
  de,
  formatQuantity,
  type Item,
  type List,
  type Store,
  type RecipeIngredient,
  type WeekPlanResult,
} from "@ainkauf/core";
import { PlanPreview } from "@/components/plan/PlanPreview";
import { RecipeCover } from "@/components/plan/RecipeCover";
import { RecipeOrigin } from "@/components/plan/RecipeOrigin";
import {
  addIngredient,
  addMealPlanEntry,
  allLists,
  allStores,
  deleteIngredient,
  deleteRecipe,
  ingredientToList,
  ingredientsOfRecipe,
  itemsOfList,
  planToListSortiert,
  previewRecipe,
  recipeById,
  updateIngredient,
  updateRecipe,
} from "@/db/repository";
import { useNavigate } from "@/lib/router";

/**
 * Ein Rezept: Name, Personenzahl, Zutaten.
 *
 * Zwei Knöpfe unten, und sie tun verschiedene Dinge: "Einplanen" legt das
 * Essen auf heute, "Zutaten auf die Liste" kauft dafür ein. Beides hängt
 * nicht aneinander, weil man auch spontan kochen kann, ohne zu planen.
 */
export function RecipeScreen({ recipeId }: { recipeId: string }) {
  const go = useNavigate();
  const [neu, setNeu] = useState("");
  const [vorschau, setVorschau] = useState<WeekPlanResult | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const rezept = useLiveQuery(() => recipeById(recipeId), [recipeId], null);
  const zutaten = useLiveQuery(() => ingredientsOfRecipe(recipeId), [recipeId], [] as RecipeIngredient[]);
  const listen = useLiveQuery(() => allLists(), [], [] as List[]);
  // Für die Vorschau: Eine Liste kann über ihren Laden zu einem Ladentyp gehören.
  const laeden = useLiveQuery(() => allStores(), [], [] as Store[]);

  // Der Warenkorb an der Zeile schreibt auf die Standardliste, ohne zu
  // fragen: Ein Tipp soll ein Tipp bleiben. Welche das ist, sagt die
  // Rückmeldung danach.
  const zielliste = listen.find((liste) => liste.is_default) ?? listen[0] ?? null;
  const aufDerListe = useLiveQuery(
    async () => (zielliste ? (await itemsOfList(zielliste.id)).filter((eintrag) => !eintrag.is_checked) : []),
    [zielliste?.id],
    [] as Item[],
  );
  const stehtSchonDrauf = (zutat: RecipeIngredient) =>
    aufDerListe.some((eintrag) => eintrag.normalized_name === zutat.normalized_name);

  async function zutatAufDieListe(zutat: RecipeIngredient) {
    if (!zielliste) return;
    const angelegt = await ingredientToList(zutat.id, zielliste.id);
    if (angelegt) melde(de.recipes.ingredientAdded(zutat.name, zielliste.name));
  }

  function melde(text: string) {
    setMeldung(text);
    window.setTimeout(() => {
      setMeldung(null);
    }, 4000);
  }

  if (rezept === null) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 pt-5">
        <Zurueck go={go} />
        <p className="karte px-5 py-10 text-center text-ink-2">{de.errors.notFound}</p>
      </div>
    );
  }

  /*
    Gespiegelte Rezepte gehören der Spaisekammer: kein Stift, kein Löschen,
    keine neue Zutat. Die einzige Ausnahme ist "Immer im Haus", damit Salz und
    Öl nicht auf die Liste wandern; das ist eine Entscheidung dieser Familie
    und keine Änderung am Rezept.
  */
  const gespiegelt = rezept.source === "spaisekammer";

  /**
   * Feld zuerst leeren, dann schreiben.
   *
   * Andersherum wischt das Leeren weg, was in der Zwischenzeit getippt wurde:
   * Das Schreiben geht durch IndexedDB und dauert einen Moment, und wer zügig
   * Zutaten eintippt, ist längst beim nächsten Wort.
   */
  async function zutatAnlegen() {
    const eingabe = neu.trim();
    if (eingabe.length === 0) return;
    setNeu("");
    await addIngredient(recipeId, eingabe);
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 pb-32">
      <header className="flex items-center gap-2 pt-5">
        <Zurueck go={go} />
        {gespiegelt ? (
          <h1 className="min-w-0 flex-1 truncate text-[1.75rem]" data-testid="recipe-title">
            {rezept.name}
          </h1>
        ) : (
          <input
            value={rezept.name}
            onChange={(event) => void updateRecipe(recipeId, { name: event.target.value })}
            aria-label={de.recipes.nameLabel}
            data-testid="recipe-title"
            className="min-h-[44px] min-w-0 flex-1 bg-transparent font-display text-[1.75rem] font-extrabold tracking-tight text-ink outline-none"
          />
        )}
      </header>

      {gespiegelt && (
        <div className="flex items-center gap-3">
          <RecipeCover recipe={rezept} size={72} />
          <div className="min-w-0 flex-1">
            <RecipeOrigin recipe={rezept} />
            <p className="mt-1 text-sm text-ink-3" data-testid="recipe-readonly">
              {de.spaisekammer.readOnly}
            </p>
          </div>
        </div>
      )}

      {rezept.note !== null && rezept.note.length > 0 && (
        <p className="text-ink-2" data-testid="recipe-note">
          {rezept.note}
        </p>
      )}

      <div className="flex items-center gap-3">
        {gespiegelt ? (
          <p className="num min-h-[44px] content-center text-sm text-ink-2" data-testid="recipe-servings">
            {de.week.servingsOf(rezept.servings)}
          </p>
        ) : (
          <label className="flex items-center gap-2 text-sm text-ink-2">
            {de.recipes.servingsLabel}
            <input
              value={String(rezept.servings)}
              onChange={(event) => {
                const zahl = Number.parseInt(event.target.value, 10);
                if (Number.isFinite(zahl) && zahl >= 1 && zahl <= 50) void updateRecipe(recipeId, { servings: zahl });
              }}
              inputMode="numeric"
              data-testid="recipe-servings"
              className="num min-h-[44px] w-16 rounded-[var(--radius-lg)] bg-paper-2 px-3 text-center text-base font-bold text-ink outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
        )}

        {gespiegelt && rezept.external_url !== null && (
          <a
            href={rezept.external_url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="recipe-external"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2"
          >
            <ExternalLink size={15} strokeWidth={1.75} aria-hidden />
            {de.spaisekammer.open}
          </a>
        )}

        {rezept.source_url !== null && (
          <a
            href={rezept.source_url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="recipe-source"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2"
          >
            <ExternalLink size={15} strokeWidth={1.75} aria-hidden />
            {de.recipes.sourceLabel}
          </a>
        )}
      </div>

      {meldung !== null && (
        <p className="rounded-[var(--radius-lg)] bg-herb/12 px-4 py-3 text-sm font-semibold text-herb" data-testid="recipe-toast">
          {meldung}
        </p>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-lg">{de.recipes.ingredients}</h2>

        {!gespiegelt && (
          <div className="flex gap-2">
            <input
              value={neu}
              onChange={(event) => {
                setNeu(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") void zutatAnlegen();
              }}
              placeholder={de.recipes.ingredientPlaceholder}
              aria-label={de.recipes.addIngredient}
              data-testid="ingredient-input"
              className="min-h-[52px] min-w-0 flex-1 rounded-[var(--radius-lg)] bg-card px-4 text-base text-ink shadow-[var(--shadow-card)] outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              type="button"
              onClick={() => void zutatAnlegen()}
              aria-label={de.recipes.addIngredient}
              data-testid="ingredient-add"
              className="inline-flex size-[52px] shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-accent text-white shadow-[var(--shadow-card)] transition-transform active:scale-95"
            >
              <Plus size={22} strokeWidth={2.25} aria-hidden />
            </button>
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {zutaten.map((zutat, i) => (
            <li
              key={zutat.id}
              className="auftritt karte flex min-h-[60px] items-center gap-2 px-3"
              data-testid="ingredient-row"
              data-name={zutat.name}
              style={{ ["--takt" as string]: `${String(Math.min(i * 25, 200))}ms` }}
            >
              <span className="min-w-0 flex-1 pl-1">
                <span className={`block truncate font-medium ${zutat.is_staple ? "text-ink-3" : ""}`}>{zutat.name}</span>
                <span className="num block truncate text-sm text-ink-2">
                  {zutat.is_staple ? de.recipes.staple : formatQuantity({ quantity: zutat.quantity, unit: zutat.unit })}
                </span>
              </span>

              {/*
                Der Warenkorb steht vor dem Schalter: Er ist der Griff, den
                man im Laden oder beim Kochen wirklich braucht. Schon drauf
                heißt Haken und kein zweiter Tipp; zwei Zeilen "Milch" helfen
                niemandem.
              */}
              <button
                type="button"
                onClick={() => void zutatAufDieListe(zutat)}
                disabled={!zielliste || stehtSchonDrauf(zutat)}
                aria-label={stehtSchonDrauf(zutat) ? de.recipes.ingredientOnList(zutat.name) : de.recipes.ingredientToList(zutat.name)}
                data-testid="ingredient-to-list"
                data-name={zutat.name}
                data-on-list={stehtSchonDrauf(zutat) ? "ja" : "nein"}
                className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90 disabled:active:scale-100 ${
                  stehtSchonDrauf(zutat) ? "bg-herb/20 text-herb" : "bg-accent/10 text-accent"
                }`}
              >
                {stehtSchonDrauf(zutat) ? (
                  <Check size={20} strokeWidth={2.5} aria-hidden />
                ) : (
                  <ShoppingCart size={20} strokeWidth={1.75} aria-hidden />
                )}
              </button>

              <button
                type="button"
                onClick={() => void updateIngredient(zutat.id, { is_staple: !zutat.is_staple })}
                aria-pressed={zutat.is_staple}
                aria-label={`${de.recipes.staple}: ${zutat.name}`}
                data-testid="ingredient-staple"
                data-name={zutat.name}
                className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90 ${
                  zutat.is_staple ? "bg-honey/25 text-honey" : "bg-paper-2 text-ink-3"
                }`}
              >
                <span aria-hidden className="text-base font-bold">
                  S
                </span>
              </button>

              {!gespiegelt && (
                <button
                  type="button"
                  onClick={() => void deleteIngredient(zutat.id)}
                  aria-label={`${de.common.delete}: ${zutat.name}`}
                  data-testid="ingredient-delete"
                  data-name={zutat.name}
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-3 transition-transform active:scale-90"
                >
                  <X size={16} strokeWidth={2} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>

        {zutaten.length > 0 && <p className="text-xs text-ink-3">{de.recipes.stapleHint}</p>}
      </section>

      <div className="flex flex-col gap-2 pt-2">
        <button
          type="button"
          onClick={() => {
            void previewRecipe(recipeId).then(setVorschau);
          }}
          disabled={zutaten.length === 0}
          data-testid="recipe-to-list"
          className="min-h-[52px] rounded-[var(--radius-xl)] bg-accent px-5 font-display text-lg font-extrabold text-white shadow-[var(--shadow-card)] transition-transform active:scale-[0.99] disabled:opacity-40"
        >
          {de.recipes.toList}
        </button>

        <button
          type="button"
          onClick={() => {
            void addMealPlanEntry(asDay(new Date()), { recipeId }).then(() => {
              melde(de.week.today);
            });
          }}
          data-testid="recipe-plan-today"
          className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-[var(--radius-xl)] bg-paper-2 px-5 font-semibold text-ink-2"
        >
          <CalendarPlus size={18} strokeWidth={1.75} aria-hidden />
          {de.recipes.plan}
        </button>

        {!gespiegelt && (
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(de.recipes.deleteConfirm(rezept.name))) return;
              void deleteRecipe(recipeId).then(() => {
                go("/planen");
              });
            }}
            data-testid="recipe-delete"
            className="inline-flex min-h-[44px] items-center justify-center gap-2 self-center rounded-full px-4 text-sm font-semibold text-accent"
          >
            <Trash2 size={16} strokeWidth={1.75} aria-hidden />
            {de.recipes.delete}
          </button>
        )}
      </div>

      {vorschau !== null && (
        <PlanPreview
          plan={vorschau}
          lists={listen}
          stores={laeden}
          onConfirm={async (listId, zeilen) => {
            const { angelegt, verteilt } = await planToListSortiert({ ...vorschau, missing: zeilen }, listId);
            setVorschau(null);
            // Was in eine andere Liste wanderte, wird gesagt: Sonst sucht
            // jemand die Sojasauce im Supermarkt.
            melde(verteilt.length > 0 ? `${de.planning.added(angelegt)}, ${de.items.movedSeveral(verteilt.length)}` : de.planning.added(angelegt));
          }}
          onCancel={() => {
            setVorschau(null);
          }}
        />
      )}
    </div>
  );
}

function Zurueck({ go }: { go: (path: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        go("/planen");
      }}
      aria-label={de.common.back}
      data-testid="recipe-back"
      className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
    >
      <ChevronLeft size={22} strokeWidth={1.5} aria-hidden />
    </button>
  );
}
